import { ChevronLeft, ChevronRight, Loader2 } from 'lucide-react';
import React, { useEffect, useMemo, useState } from 'react';
import { ApiService } from '../../../services/api';
import type { PracticeExam } from '../../../types/practice-exam';
import { IMAGE_SCALE } from './ExamQuestionSolver';

type AnswerStatus = 'correct' | 'wrong' | 'blank' | null;
type QuestionFilter = 'all' | 'wrong' | 'blank';

interface SolutionQuestion {
  subjectName: string;
  questionNumber: number;
  printedNumber: number;
  imageUrl: string | null;
  correctAnswer: string | null;
  studentAnswer: string | null;
  status: AnswerStatus;
  topic: string | null;
  achievement: string | null;
  solutionText: string | null;
}

interface SolutionTest {
  testId: number;
  label: string;
  segment: 'tyt' | 'ayt' | null;
  questions: SolutionQuestion[];
}

// MEB'in yayın şartı: kaynak ve ücretsizlik, deneme nereden açılırsa açılsın listede ve çözüm ekranında yazılıdır
export const freeExamSourceNote = (levelType?: string | null) =>
  `MEBİ ${levelType === 'lgs' ? 'LGS' : 'YKS'} Denemeleri, Millî Eğitim Bakanlığı. Ücretsizdir, ticari amaçla yayınlanmamıştır.`;

const SEGMENT_LABELS = { tyt: 'TYT', ayt: 'AYT' };

const PALETTE_STYLES: Record<string, string> = {
  correct: 'border-emerald-200 bg-emerald-50 text-emerald-800',
  wrong: 'border-rose-200 bg-rose-50 text-rose-800',
  blank: 'border-gray-200 bg-gray-100 text-gray-600',
  none: 'border-gray-200 bg-white text-gray-700',
};

const countByStatus = (test: SolutionTest | undefined, status: AnswerStatus) =>
  (test?.questions || []).filter((question) => question.status === status).length;

function QuestionImage({ question, label }: { question: SolutionQuestion; label: string }) {
  const [width, setWidth] = useState<number | null>(null);
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    setFailed(false);
  }, [question.imageUrl]);

  if (!question.imageUrl || failed) {
    return <p className="py-8 text-center text-sm text-gray-500">Soru görseli açılamadı.</p>;
  }

  return (
    <img
      src={question.imageUrl}
      alt={label}
      onLoad={(event) => setWidth(event.currentTarget.naturalWidth * IMAGE_SCALE)}
      onError={() => setFailed(true)}
      style={width ? { width, maxWidth: '100%' } : { maxWidth: '100%' }}
      className="mx-auto block h-auto"
    />
  );
}

function AnswerLine({ question }: { question: SolutionQuestion }) {
  if (question.status === null) {
    return <p className="text-sm text-gray-700">Doğru cevap: <span className="font-bold text-emerald-700">{question.correctAnswer || '-'}</span></p>;
  }

  return (
    <p className="flex flex-wrap gap-x-4 gap-y-1 text-sm text-gray-700">
      <span>
        Senin cevabın:{' '}
        {question.status === 'blank'
          ? <span className="font-bold text-gray-600">Boş</span>
          : <span className={`font-bold ${question.status === 'correct' ? 'text-emerald-700' : 'text-rose-700'}`}>{question.studentAnswer}</span>}
      </span>
      <span>Doğru cevap: <span className="font-bold text-emerald-700">{question.correctAnswer || '-'}</span></span>
    </p>
  );
}

type Props = {
  exam: PracticeExam;
  onBack: () => void;
};

export default function ExamSolutions({ exam, onBack }: Props) {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [attempt, setAttempt] = useState(0);
  const [tests, setTests] = useState<SolutionTest[]>([]);
  const [hasResult, setHasResult] = useState(false);
  const [testIndex, setTestIndex] = useState(0);
  const [filter, setFilter] = useState<QuestionFilter>('all');
  const [position, setPosition] = useState(0);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError('');
    ApiService.getMyPracticeExamSolutions(exam.id)
      .then((res: any) => {
        if (cancelled) return;
        setTests(res?.tests || []);
        setHasResult(Boolean(res?.hasResult));
      })
      .catch((err: any) => {
        if (!cancelled) setError(err?.message || 'Çözümler yüklenemedi.');
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [exam.id, attempt]);

  const test = tests[testIndex];
  const shown = useMemo(
    () => (test?.questions || []).filter((question) => filter === 'all' || question.status === filter),
    [test, filter]
  );
  const question = shown[Math.min(position, shown.length - 1)];
  const questionLabel = question ? `${test.label} ${question.printedNumber}. soru` : '';

  const selectTest = (index: number) => {
    setTestIndex(index);
    setPosition(0);
  };

  const selectFilter = (next: QuestionFilter) => {
    setFilter(next);
    setPosition(0);
  };

  const filters: { key: QuestionFilter; label: string; count: number }[] = [
    { key: 'all', label: 'Tümü', count: test?.questions.length || 0 },
    { key: 'wrong', label: 'Yanlışlarım', count: countByStatus(test, 'wrong') },
    { key: 'blank', label: 'Boşlarım', count: countByStatus(test, 'blank') },
  ];

  return (
    <div className="flex h-full flex-col gap-3 overflow-y-auto p-3 sm:gap-4 sm:p-6">
      <div className="flex items-center gap-2 sm:gap-3">
        <button type="button" onClick={onBack} aria-label="Geri" className="rounded-full p-2 text-gray-600 transition-colors hover:bg-gray-100">
          <ChevronLeft size={22} />
        </button>
        <div className="min-w-0">
          <h2 className="truncate text-lg font-bold text-gray-900 sm:text-xl">{exam.name}</h2>
          <p className="text-sm text-gray-500">Soru çözümleri</p>
        </div>
      </div>

      <div className="rounded-lg border border-blue-100 bg-blue-50 px-3 py-2 text-xs leading-5 text-blue-950">
        <p>Yazılı çözümler yapay zekâ ile hazırlandı, hata içerebilir. Takıldığın ya da yanlış bulduğun çözümü öğretmenine sor.</p>
        {exam.free && <p className="mt-1 text-blue-900">{freeExamSourceNote(exam.levelType)}</p>}
      </div>

      {loading ? (
        <div className="flex justify-center py-12">
          <Loader2 className="h-8 w-8 animate-spin text-blue-600" />
        </div>
      ) : error ? (
        <div className="rounded-lg border border-gray-200 bg-white p-6 text-center text-sm text-gray-600">
          <p>{error}</p>
          <button type="button" onClick={() => setAttempt((current) => current + 1)} className="mt-2 font-medium text-blue-600 hover:underline">
            Yeniden dene
          </button>
        </div>
      ) : (
        <>
          <div className="-mx-3 flex shrink-0 gap-1.5 overflow-x-auto px-3 sm:mx-0 sm:flex-wrap sm:px-0">
            {tests.map((item, index) => (
              <button
                key={item.testId}
                type="button"
                onClick={() => selectTest(index)}
                className={`shrink-0 rounded-lg border px-3 py-1.5 text-sm font-medium transition-colors ${index === testIndex ? 'border-blue-600 bg-blue-600 text-white' : 'border-gray-300 bg-white text-gray-700 hover:bg-gray-50'}`}
              >
                {item.segment ? `${SEGMENT_LABELS[item.segment]} ` : ''}{item.label}
              </button>
            ))}
          </div>

          {hasResult && (
            <div className="flex gap-1.5">
              {filters.map((item) => (
                <button
                  key={item.key}
                  type="button"
                  onClick={() => selectFilter(item.key)}
                  className={`rounded-full border px-3 py-1 text-xs font-medium transition-colors ${filter === item.key ? 'border-blue-600 bg-blue-50 text-blue-700' : 'border-gray-200 bg-white text-gray-600 hover:bg-gray-50'}`}
                >
                  {item.label} ({item.count})
                </button>
              ))}
            </div>
          )}

          {!question ? (
            <div className="rounded-lg border-2 border-dashed border-gray-200 bg-gray-50 p-8 text-center text-sm text-gray-600">
              Bu testte bu seçime uyan soru yok.
            </div>
          ) : (
            <>
              <div className="flex flex-wrap gap-1">
                {shown.map((item, index) => (
                  <button
                    key={`${item.subjectName}-${item.questionNumber}`}
                    type="button"
                    onClick={() => setPosition(index)}
                    aria-label={`${item.printedNumber}. soru`}
                    className={`h-8 w-8 rounded-md border text-xs font-semibold transition-colors ${PALETTE_STYLES[item.status || 'none']} ${item === question ? 'ring-2 ring-blue-600 ring-offset-1' : ''}`}
                  >
                    {item.printedNumber}
                  </button>
                ))}
              </div>

              <div className="grid gap-3 lg:grid-cols-2">
                <div className="rounded-xl border border-gray-200 bg-white p-3">
                  <p className="mb-2 text-sm font-semibold text-gray-900">{question.printedNumber}. soru <span className="font-normal text-gray-500">· {question.subjectName}</span></p>
                  <QuestionImage key={question.imageUrl || questionLabel} question={question} label={questionLabel} />
                </div>

                <div className="space-y-3 rounded-xl border border-gray-200 bg-white p-4">
                  <AnswerLine question={question} />
                  {question.solutionText ? (
                    <p className="whitespace-pre-line text-sm leading-6 text-gray-800">{question.solutionText}</p>
                  ) : (
                    <p className="text-sm text-gray-500">Bu sorunun çözümü henüz hazır değil.</p>
                  )}
                  {(question.topic || question.achievement) && (
                    <div className="border-t border-gray-100 pt-2 text-xs leading-5 text-gray-500">
                      {question.topic && <p><span className="font-medium text-gray-600">Konu:</span> {question.topic}</p>}
                      {question.achievement && question.achievement !== question.topic && (
                        <p><span className="font-medium text-gray-600">Kazanım:</span> {question.achievement}</p>
                      )}
                    </div>
                  )}
                </div>
              </div>

              <div className="flex justify-between gap-2 pb-2">
                <button
                  type="button"
                  disabled={position === 0}
                  onClick={() => setPosition((current) => current - 1)}
                  className="inline-flex items-center gap-1 rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-40"
                >
                  <ChevronLeft size={16} /> Önceki
                </button>
                <button
                  type="button"
                  disabled={position >= shown.length - 1}
                  onClick={() => setPosition((current) => current + 1)}
                  className="inline-flex items-center gap-1 rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-40"
                >
                  Sonraki <ChevronRight size={16} />
                </button>
              </div>
            </>
          )}
        </>
      )}
    </div>
  );
}
